import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, RotateCcw, Palette, Check, Home, Sliders } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTheme } from '../../lib/theme-context';
import { TEMA_PADRAO, type ChaveTema } from '../../lib/tema';
import type { Template } from '../../lib/temas';
import { isValorTemaValido, HEX_RE } from '../../lib/cor-tema';
import { EditorCorOuGradiente } from '../../components/EditorCorOuGradiente';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api';

interface CampoCor { chave: ChaveTema; label: string }

interface GrupoCores {
  titulo: string;
  descricao: string;
  campos: CampoCor[];
}

const GRUPOS: GrupoCores[] = [
  {
    titulo: 'Layout',
    descricao: 'Pagina, paineis e barra do topo',
    campos: [
      { chave: 'page_bg', label: 'Pagina (externo) — fundo' },
      { chave: 'page_fg', label: 'Pagina (externo) — fonte' },
      { chave: 'panel_bg', label: 'Painel (interno) — fundo' },
      { chave: 'panel_fg', label: 'Painel (interno) — fonte' },
      { chave: 'app_header_bg', label: 'Barra do topo — fundo' },
      { chave: 'app_header_fg', label: 'Barra do topo — fonte' },
      { chave: 'page_header_bg', label: 'Titulo da pagina — fundo' },
      { chave: 'page_header_fg', label: 'Titulo da pagina — fonte' },
    ],
  },
  {
    titulo: 'Modais',
    descricao: 'Janelas e dialogos',
    campos: [
      { chave: 'modal_bg', label: 'Corpo — fundo' },
      { chave: 'modal_fg', label: 'Corpo — fonte' },
      { chave: 'modal_header_bg', label: 'Cabecalho — fundo' },
      { chave: 'modal_header_fg', label: 'Cabecalho — fonte' },
    ],
  },
  {
    titulo: 'Cards',
    descricao: 'Blocos das paginas',
    campos: [
      { chave: 'card_bg', label: 'Fundo' },
      { chave: 'card_fg', label: 'Fonte' },
    ],
  },
  {
    titulo: 'Campos',
    descricao: 'Inputs, selects, areas de texto',
    campos: [
      { chave: 'input_bg', label: 'Fundo' },
      { chave: 'input_fg', label: 'Fonte' },
      { chave: 'input_border', label: 'Borda' },
    ],
  },
  {
    titulo: 'Menus popup',
    descricao: 'Dropdowns e menus contextuais',
    campos: [
      { chave: 'popup_bg', label: 'Fundo' },
      { chave: 'popup_fg', label: 'Fonte' },
      { chave: 'popup_border', label: 'Borda' },
    ],
  },
  {
    titulo: 'Cabecalhos de tabela',
    descricao: 'Topo das tabelas e subtitulos',
    campos: [
      { chave: 'grid_header_bg', label: 'Principal — fundo' },
      { chave: 'grid_header_fg', label: 'Principal — fonte' },
      { chave: 'grid_subheader_bg', label: 'Subcabecalho — fundo' },
      { chave: 'grid_subheader_fg', label: 'Subcabecalho — fonte' },
    ],
  },
  {
    titulo: 'Linhas de tabela',
    descricao: 'Corpo das tabelas',
    campos: [
      { chave: 'grid_row_bg', label: 'Fundo padrao' },
      { chave: 'grid_row_alt_bg', label: 'Fundo alternado' },
      { chave: 'grid_row_fg', label: 'Fonte' },
      { chave: 'grid_row_hover_bg', label: 'Destaque hover' },
    ],
  },
  {
    titulo: 'Abas das paginas',
    descricao: 'Faixa de abas (ex: Orcamentos / Finalizados / Cancelados)',
    campos: [
      { chave: 'tabs_bg', label: 'Fundo' },
      { chave: 'tabs_active_fg', label: 'Aba ativa (texto + indicador)' },
    ],
  },
  {
    titulo: 'WhatsApp',
    descricao: 'Tela de atendimento (painel lateral, chat, mensagens)',
    campos: [
      { chave: 'wa_sidebar_bg', label: 'Painel lateral — fundo' },
      { chave: 'wa_chat_header_bg', label: 'Cabecalho do contato — fundo' },
      { chave: 'wa_chat_bg', label: 'Fundo do chat' },
      { chave: 'wa_bubble_out_bg', label: 'Mensagem enviada — fundo' },
      { chave: 'wa_text_fg', label: 'Texto' },
      { chave: 'wa_subtext_fg', label: 'Sub-texto' },
    ],
  },
];

// Campos *_bg aceitam gradient (excecao: input_bg, pq select tem SVG da setinha
// em background-image). 3 *_fg de cabecalhos visuais aceitam gradient via
// background-clip: text. Cabecalhos de tabela (grid_header_fg/subheader_fg) ficam
// solido — multi-clip no <th> e fragil e quebra renderizacao.
const FG_COM_GRADIENT = new Set<ChaveTema>([
  'app_header_fg', 'page_header_fg', 'modal_header_fg',
]);
function aceitaDegrade(chave: ChaveTema): boolean {
  if (chave.endsWith('_bg')) return chave !== 'input_bg';
  return FG_COM_GRADIENT.has(chave);
}

// Mini-preview de um template (faixa do topo + corpo + card + cabecalho de tabela).
function MiniPreview({ cores }: { cores: Partial<Record<ChaveTema, string>> }) {
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', height: 66 }}>
      <div style={{ height: 18, background: cores.app_header_bg ?? '#fff' }} />
      <div style={{ height: 48, background: cores.page_bg ?? '#fff', display: 'flex', gap: 5, padding: 7 }}>
        <div style={{ flex: 1, background: cores.card_bg ?? '#fff', borderRadius: 4, border: `1px solid ${cores.input_border ?? '#ddd'}` }} />
        <div style={{ width: 24, background: cores.grid_header_bg ?? '#333', borderRadius: 4 }} />
      </div>
    </div>
  );
}

interface CardTemplateProps {
  nome: string; descricao: string; cores: Partial<Record<ChaveTema, string>>;
  ativo: boolean; ehPadrao: boolean; podeConfigurar: boolean;
  onAplicar: () => void; onDefinirLoja?: () => void; onSoMeuPC?: () => void;
}
function CardTemplate(p: CardTemplateProps) {
  return (
    <div style={{
      border: p.ativo ? '2px solid var(--accent, #b45309)' : '1px solid var(--border)',
      borderRadius: 12, padding: 12, background: 'var(--card-bg)',
    }}>
      <MiniPreview cores={p.cores} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{p.nome}</strong>
        {p.ativo && (
          <span className="badge" style={{ background: 'var(--accent-soft, #fbeedd)', color: 'var(--accent, #b45309)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Check size={11} /> em uso
          </span>
        )}
        {p.ehPadrao && (
          <span className="badge" style={{ background: 'var(--positive-bg, #fde68a)', color: 'var(--positive-fg, #000)' }}>padrao da casa</span>
        )}
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '5px 0 9px' }}>{p.descricao}</p>
      {p.podeConfigurar ? (
        // Admin/gerente: botao principal define pra loja toda; link discreto pra so este PC.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            type="button"
            className="btn-primario"
            style={{ padding: '6px 12px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={p.onDefinirLoja}
          >
            <Home size={13} /> Definir como tema da loja
          </button>
          <button
            type="button"
            className="btn-link"
            style={{ fontSize: 12, padding: '2px 0', textAlign: 'left', color: 'var(--page-fg)', opacity: 0.6 }}
            onClick={p.onSoMeuPC}
          >
            usar so no meu computador
          </button>
        </div>
      ) : (
        // Nao-admin: so pode usar pra si mesmo.
        <button type="button" className="btn-primario" style={{ padding: '6px 12px', fontSize: 13 }} onClick={p.onAplicar}>Usar pra mim</button>
      )}
    </div>
  );
}

interface CoresProps { onFechar?: () => void }

export function Cores({ onFechar }: CoresProps = {}) {
  const { tema, atualizar, preview, resetPreview, templates, templateAtivoId, escolherTemplate, definirTemaDaLoja } = useTheme();
  const { usuario } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState<Partial<Record<ChaveTema, string>>>({});
  const [salvando, setSalvando] = useState(false);
  const [padraoCasa, setPadraoCasa] = useState<string | null>(null);
  const podeConfigurar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  useEffect(() => {
    setForm(tema);
  }, [tema]);

  // Carrega qual e o template "padrao da casa" (app_settings) pra marcar na UI.
  useEffect(() => {
    api.get<{ valor: string | null }>('/api/app-settings/tema_template_padrao')
      .then((r) => setPadraoCasa(r?.valor ?? null))
      .catch(() => setPadraoCasa(null));
  }, []);

  async function aplicarTemaDaLoja(id: string) {
    try {
      await definirTemaDaLoja(id);
      setPadraoCasa(id);
      toast.sucesso('tema da loja atualizado');
    } catch (e: any) {
      toast.erro(e.message ?? 'falha ao definir tema da loja');
    }
  }

  function setCor(chave: ChaveTema, valor: string) {
    const novo = { ...form, [chave]: valor };
    setForm(novo);
    if (isValorTemaValido(valor)) preview({ [chave]: valor });
  }

  async function salvar() {
    setSalvando(true);
    try {
      // Manda so o que mudou em relacao ao tema atual
      const parcial: Partial<Record<ChaveTema, string>> = {};
      for (const k of Object.keys(form) as ChaveTema[]) {
        const v = form[k];
        if (v && v !== tema[k] && isValorTemaValido(v)) parcial[k] = v;
      }
      if (Object.keys(parcial).length === 0) {
        toast.aviso('nenhuma cor alterada');
        return;
      }
      await atualizar(parcial);
      toast.sucesso('cores salvas');
    } catch (e: any) {
      toast.erro(e.message ?? 'falha ao salvar');
      resetPreview();
    } finally {
      setSalvando(false);
    }
  }

  function restaurarPadrao() {
    setForm(TEMA_PADRAO);
    preview(TEMA_PADRAO);
  }

  function descartar() {
    setForm(tema);
    resetPreview();
  }

  function fechar() {
    resetPreview();
    if (onFechar) onFechar();
    else navigate('/');
  }

  // ESC fecha quando aberto como modal flutuante (onFechar custom)
  useEffect(() => {
    if (!onFechar) return;
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') fechar(); }
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFechar]);

  const flutuante = !!onFechar;

  const wrapper = (filhos: ReactNode): ReactNode => {
    if (flutuante) {
      // Modal flutuante: overlay sem backdrop escuro (pointer-events:none) pra
      // nao ofuscar a tela embaixo. So o painel recebe cliques. Painel arrastavel
      // automaticamente via .modal-conteudo + .modal-header (modal-drag global).
      return (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 60,
            pointerEvents: 'none',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            paddingTop: 56,
          }}
        >
          <div style={{ pointerEvents: 'auto', width: 'min(95vw, 720px)' }}>{filhos}</div>
        </div>
      );
    }
    return <div style={{ display: 'flex', justifyContent: 'center' }}>{filhos}</div>;
  };

  return wrapper(
      <div className="modal-conteudo" style={{ maxWidth: flutuante ? 720 : 1100 }}>
        <div className="modal-header">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Palette size={18} />
            Aparencia
          </span>
          <button type="button" className="btn-fechar-modal" onClick={fechar} aria-label="Fechar">×</button>
        </div>

        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>
            {podeConfigurar
              ? <>Clique em <b>Definir como tema da loja</b> para trocar o tema de todos os computadores de uma vez. Use <b>usar so no meu computador</b> para testar sem afetar a equipe. O botao sol/lua no topo alterna claro/escuro a qualquer momento.</>
              : <>Escolha um tema pronto. A escolha vale so neste computador. O botao sol/lua no topo alterna claro/escuro a qualquer momento.</>
            }
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 8 }}>
            {templates.map((t: Template) => (
              <CardTemplate
                key={t.id}
                nome={t.nome}
                descricao={t.descricao}
                cores={t.cores}
                ativo={templateAtivoId === t.id}
                ehPadrao={padraoCasa === t.id}
                podeConfigurar={podeConfigurar}
                onAplicar={() => { escolherTemplate(t.id); toast.sucesso(`tema "${t.nome}" aplicado`); }}
                onDefinirLoja={() => void aplicarTemaDaLoja(t.id)}
                onSoMeuPC={() => { escolherTemplate(t.id); toast.sucesso(`tema "${t.nome}" aplicado neste computador`); }}
              />
            ))}
            <CardTemplate
              nome="Personalizado (legado)"
              descricao="O tema customizado atual da loja. Mantido pra compatibilidade."
              cores={TEMA_PADRAO}
              ativo={templateAtivoId === 'personalizado'}
              ehPadrao={padraoCasa === 'personalizado' || padraoCasa === null}
              podeConfigurar={podeConfigurar}
              onAplicar={() => { escolherTemplate('personalizado'); toast.sucesso('tema personalizado aplicado'); }}
              onDefinirLoja={() => void aplicarTemaDaLoja('personalizado')}
              onSoMeuPC={() => { escolherTemplate('personalizado'); toast.sucesso('tema personalizado aplicado neste computador'); }}
            />
          </div>

          {podeConfigurar && (
            <details style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 10, padding: '0 14px' }}>
              <summary style={{ cursor: 'pointer', padding: '12px 0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sliders size={16} /> Personalizar avancado (cor por cor)
              </summary>
              <p className="muted" style={{ marginTop: 0 }}>
                Ajuste fino salvo no tema <b>Personalizado (legado)</b>. Mudancas aparecem ao vivo; so persistem ao clicar Salvar.
              </p>
              {GRUPOS.map((grupo) => (
                <div key={grupo.titulo} className="cores-grupo">
                  <h3>{grupo.titulo}</h3>
                  <p className="descr">{grupo.descricao}</p>
                  <div className="cores-grid">
                    {grupo.campos.map((c) => {
                      const valor = form[c.chave] ?? TEMA_PADRAO[c.chave];
                      return (
                        <div key={c.chave} className="cor-row">
                          <label htmlFor={c.chave}>{c.label}</label>
                          {aceitaDegrade(c.chave) ? (
                            <EditorCorOuGradiente
                              valor={valor}
                              onChange={(v) => setCor(c.chave, v)}
                            />
                          ) : (
                            <>
                              <input
                                id={c.chave}
                                type="color"
                                value={HEX_RE.test(valor) ? valor : '#000000'}
                                onChange={(e) => setCor(c.chave, e.target.value)}
                              />
                              <input
                                type="text"
                                value={valor}
                                onChange={(e) => setCor(c.chave, e.target.value)}
                                maxLength={7}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </details>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secundario" onClick={restaurarPadrao} disabled={salvando}>
            <RotateCcw size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> Padrao
          </button>
          <button type="button" className="btn-secundario" onClick={descartar} disabled={salvando}>Descartar</button>
          <button type="button" className="btn-secundario" onClick={fechar} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={salvar} disabled={salvando}>
            <Save size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
  );
}

export function ModalCores({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  if (!aberto) return null;
  return <Cores onFechar={onFechar} />;
}
